import {
  ArrowRight20Regular,
  BrainCircuit20Regular,
  CheckmarkCircle16Filled,
  Database20Regular,
  Eye20Regular,
  Fingerprint20Regular,
  FolderOpen20Regular,
  Key20Regular,
  Laptop20Regular,
  LockClosed20Regular,
  Search20Regular,
  Server20Regular,
  ShieldCheckmark20Regular,
} from '@fluentui/react-icons'
import { DetailSection, NextPage, PageHero, SubNav } from '../components/PageChrome'
import { Reveal } from '../components/Reveal'
import { SpotlightCard } from '../components/Primitives'

const controls = [
  {
    icon: <Key20Regular />,
    title: 'User identity',
    text: 'Keycloak authenticates office users over OIDC and supplies the administrator and user roles. Passwords never pass through Libris.',
    points: ['Authorization Code + PKCE', 'Optional multi-factor authentication', 'Short-lived access tokens', 'Issuer, audience, signature and expiry validated per call'],
  },
  {
    icon: <Fingerprint20Regular />,
    title: 'Endpoint identity',
    text: 'A signed-in person does not automatically make a workstation trusted. Enrolled agents prove machine identity separately.',
    points: ['Client-certificate enrollment', 'Mutual TLS', 'Certificate rotation and revocation', 'Signed request envelopes'],
  },
  {
    icon: <FolderOpen20Regular />,
    title: 'File authorization',
    text: 'Seeing a catalog entry and opening the file are two different decisions, and both are evaluated against current source state.',
    points: ['Windows ACL revalidation at access time', 'Online / offline availability checks', 'Policy-approved metadata only', 'Similarity is never an authorization signal'],
  },
  {
    icon: <Database20Regular />,
    title: 'Data protection',
    text: 'Local and central stores sit on different trust boundaries and hold only what their deployment mode requires.',
    points: ['SQLite catalog per workstation', 'Isolated PostgreSQL on the server', 'DPAPI-protected local secrets', 'Encrypted transport in both directions'],
  },
]

const authFlow = [
  { icon: <Laptop20Regular />, name: 'Libris Desktop', role: 'Starts sign-in with PKCE' },
  { icon: <Key20Regular />, name: 'Keycloak', role: 'Authenticates the user and MFA' },
  { icon: <Server20Regular />, name: 'Libris Server', role: 'Validates token, audience and role' },
  { icon: <ShieldCheckmark20Regular />, name: 'Authorized API', role: 'Applies policy and ACL scope' },
]

const vectorStages = [
  {
    icon: <BrainCircuit20Regular />,
    title: 'Creation',
    text: 'Only permitted extracted text is sent to the configured embedding provider, never whole files, and never without an administrator enabling it.',
  },
  {
    icon: <Database20Regular />,
    title: 'Storage',
    text: 'Local vectors stay in the workstation catalog. Managed vectors live in pgvector inside the protected central database.',
  },
  {
    icon: <Search20Regular />,
    title: 'Retrieval',
    text: 'Similarity produces candidates. Source policy and current permissions filter those candidates before a result reaches anyone.',
  },
  {
    icon: <Eye20Regular />,
    title: 'Evidence',
    text: 'Every result keeps its document, page, model and source provenance, so a ranking can always be explained after the fact.',
  },
]

const auditRecords: [string, string][] = [
  ['Authentication', 'Successful and rejected identity events, with issuer and role claims'],
  ['Administration', 'User, role, policy, shared-root and endpoint changes'],
  ['File operations', 'Proposal, approval, execution, verification and rollback'],
  ['Synchronization', 'Signed envelopes, cursor state, conflicts and rejections'],
  ['AI processing', 'Provider, model, policy decision and source provenance'],
]

export function Security() {
  return (
    <main id="main">
      <PageHero
        kicker="Security architecture"
        title="Trust the user, the endpoint and the source — not the score."
        lead="Libris applies identity and authorization controls before search relevance, vector similarity or automation is allowed to expose or change any information."
        specs={[
          ['Identity', 'Keycloak · OIDC · PKCE'],
          ['Endpoint', 'Client certificate · mTLS'],
          ['Authorization', 'Live Windows ACL revalidation'],
          ['Evidence', 'Audit journal · rollback'],
        ]}
      />

      <section className="principle-band inverted">
        <div className="shell">
          <Reveal as="p" variant="fade" className="kicker">
            Core principle
          </Reveal>
          <Reveal as="blockquote" variant="mask" className="principle-large" index={1}>
            Search may rank a document. It never grants permission to see it.
          </Reveal>
        </div>
      </section>

      <SubNav
        items={[
          { id: 'layers', label: 'Control layers' },
          { id: 'signin', label: 'Managed sign-in' },
          { id: 'vectors', label: 'Semantic privacy' },
          { id: 'audit', label: 'Audit evidence' },
        ]}
      />

      <DetailSection
        id="layers"
        index="01"
        title="Four independent control layers"
        lead="A request has to satisfy every layer that applies to it. Failing one is enough to stop the request."
      >
        <div className="card-grid is-two">
          {controls.map((control, index) => (
            <SpotlightCard key={control.title} index={index % 2}>
              <span className="card-index">0{index + 1}</span>
              <span className="card-icon">{control.icon}</span>
              <h3>{control.title}</h3>
              <p className="body">{control.text}</p>
              <ul className="card-points">
                {control.points.map(point => (
                  <li key={point}>
                    <CheckmarkCircle16Filled />
                    {point}
                  </li>
                ))}
              </ul>
            </SpotlightCard>
          ))}
        </div>
      </DetailSection>

      <DetailSection
        id="signin"
        index="02"
        title="Managed sign-in"
        lead="Keycloak is the office identity authority. Libris stays responsible for application authorization."
        tinted
      >
        <div className="auth-flow">
          {authFlow.map((step, index) => (
            <Reveal key={step.name} as="div" className="auth-node" variant="up" index={index}>
              <span className="auth-icon">{step.icon}</span>
              <b>{step.name}</b>
              <small>{step.role}</small>
              {index < authFlow.length - 1 && (
                <i className="auth-arrow" aria-hidden="true">
                  <ArrowRight20Regular />
                </i>
              )}
            </Reveal>
          ))}
        </div>
        <Reveal className="callout" variant="up">
          <LockClosed20Regular />
          <p className="body">
            <b>Credentials never reach Libris.</b> Keycloak owns password validation. Libris validates the issuer,
            audience, signature, expiration and required role claims on every protected operation, and re-checks
            filesystem permissions before content is returned.
          </p>
        </Reveal>
      </DetailSection>

      <DetailSection
        id="vectors"
        index="03"
        title="Vectors and semantic privacy"
        lead="Embeddings improve ranking. They are treated as derived sensitive data, not as a shortcut around permissions."
      >
        <div className="stage-grid">
          {vectorStages.map((stage, index) => (
            <Reveal as="article" key={stage.title} variant="up" index={index}>
              <span className="card-icon">{stage.icon}</span>
              <h3>{stage.title}</h3>
              <p className="body">{stage.text}</p>
            </Reveal>
          ))}
        </div>
      </DetailSection>

      <section id="audit" className="band inverted">
        <div className="shell">
          <div className="section-head">
            <Reveal as="span" variant="fade" className="section-index mono">
              §&nbsp;04
            </Reveal>
            <div>
              <Reveal as="h2" variant="mask" className="display-2">
                Operations leave evidence.
              </Reveal>
              <Reveal as="p" variant="up" className="lead" index={1}>
                High-impact actions are reviewable before they run and after they finish. Nothing consequential happens
                without a record of who approved it and what changed.
              </Reveal>
            </div>
          </div>
          <dl className="audit-list">
            {auditRecords.map(([term, value], index) => (
              <Reveal as="div" key={term} variant="up" index={index}>
                <dt className="mono">{term}</dt>
                <dd>{value}</dd>
              </Reveal>
            ))}
          </dl>
        </div>
      </section>

      <section className="band-tight">
        <div className="shell">
          <NextPage href="/features" label="Next" title="The full feature inventory" />
        </div>
      </section>
    </main>
  )
}
